import { marketplaceService } from '../services/marketplace';
import { downloadBlobToDevice, type FileDownloadResult } from '../services/fileDownload';
import type {
    ShipmentContract,
    ShipmentContractFile,
    ShipmentContractFileCategory,
    ShipmentContractFileScope,
    ShipmentContractListStatusFilter,
    MarketplacePaginationMeta,
} from '../types/marketplace';
import { normalizeCollection, toErrorMessage, type MarketplaceGet, type MarketplaceSet } from './marketplaceStoreTypes';

const DEFAULT_CONTRACTS_PER_PAGE = 10;

function contractMatchesFilter(contract: ShipmentContract, filter: ShipmentContractListStatusFilter): boolean {
    if (filter === 'all') {
        return true;
    }

    return contract.status === filter;
}

function mergeById<T extends { id: number }>(current: T[], incoming: T[]): T[] {
    const merged = new Map<number, T>();

    for (const item of current) {
        merged.set(item.id, item);
    }

    for (const item of incoming) {
        merged.set(item.id, item);
    }

    return Array.from(merged.values());
}

function mergeContractIntoList(
    list: ShipmentContract[],
    contract: ShipmentContract,
    filter: ShipmentContractListStatusFilter
): ShipmentContract[] {
    const withoutCurrent = list.filter((item) => item.id !== contract.id);

    if (!contractMatchesFilter(contract, filter)) {
        return withoutCurrent;
    }

    return [contract, ...withoutCurrent];
}

export function createContractSlice(set: MarketplaceSet, get: MarketplaceGet) {
    return {
        myContracts: [] as ShipmentContract[],
        myContractsMeta: null as MarketplacePaginationMeta<ShipmentContractListStatusFilter> | null,
        myContractsStatusFilter: 'all' as ShipmentContractListStatusFilter,
        selectedContract: null as ShipmentContract | null,
        isLoadingContracts: false,
        isSavingContract: false,
        isSendingInvitation: false,
        contractFiles: [] as ShipmentContractFile[],
        isLoadingContractFiles: false,
        isUploadingContractFiles: false,
        isDeletingContractFile: false,
        isDownloadingContractFile: false,

        fetchMyContracts: async ({
            page = 1,
            perPage = DEFAULT_CONTRACTS_PER_PAGE,
            status = 'all',
            append = false,
        }: {
            page?: number;
            perPage?: number;
            status?: ShipmentContractListStatusFilter;
            append?: boolean;
        } = {}): Promise<void> => {
            set((state) => ({
                isLoadingContracts: true,
                error: null,
                myContractsStatusFilter: status,
                myContracts: append ? state.myContracts : [],
                myContractsMeta: append ? state.myContractsMeta : null,
            }));

            try {
                const response = await marketplaceService.getMyContracts({
                    page,
                    per_page: perPage,
                    status: status === 'all' ? undefined : status,
                });

                const nextPageContracts = normalizeCollection<ShipmentContract>(response.data);

                set((state) => ({
                    myContracts: append ? mergeById(state.myContracts, nextPageContracts) : nextPageContracts,
                    myContractsMeta: response.meta,
                    myContractsStatusFilter: status,
                    isLoadingContracts: false,
                }));
            } catch (error) {
                set({
                    isLoadingContracts: false,
                    error: toErrorMessage(error, 'No se pudieron cargar tus contratos.'),
                });
            }
        },

        fetchContractDetail: async (contractId: number): Promise<ShipmentContract | null> => {
            set({ isLoadingContracts: true, error: null });

            try {
                const response = await marketplaceService.getContractDetail(contractId);
                const contract = response.data;

                set({
                    selectedContract: contract,
                    isLoadingContracts: false,
                });

                return contract;
            } catch (error) {
                set({
                    isLoadingContracts: false,
                    error: toErrorMessage(error, 'No se pudo cargar el detalle del contrato.'),
                });
                return null;
            }
        },

        assignContractResources: async (
            contractId: number,
            payload: Parameters<typeof marketplaceService.createContractAssignment>[1]
        ): Promise<ShipmentContract | null> => {
            set({ isSavingContract: true, error: null });

            try {
                await marketplaceService.createContractAssignment(contractId, payload);
                const response = await marketplaceService.getContractDetail(contractId);
                const contract = response.data;

                set((state) => ({
                    selectedContract: contract,
                    myContracts: mergeContractIntoList(
                        state.myContracts,
                        contract,
                        state.myContractsStatusFilter
                    ),
                    isSavingContract: false,
                }));

                return contract;
            } catch (error) {
                set({
                    isSavingContract: false,
                    error: toErrorMessage(error, 'No se pudo guardar la asignación operativa del contrato.'),
                });
                return null;
            }
        },

        fetchContractFiles: async (contractId: number): Promise<ShipmentContractFile[]> => {
            set({ isLoadingContractFiles: true, error: null });

            try {
                const response = await marketplaceService.getContractFiles(contractId);
                const contractFiles = normalizeCollection<ShipmentContractFile>(response.data);

                set({
                    contractFiles,
                    isLoadingContractFiles: false,
                });

                return contractFiles;
            } catch (error) {
                set({
                    contractFiles: [],
                    isLoadingContractFiles: false,
                    error: toErrorMessage(error, 'No se pudieron cargar los archivos del contrato.'),
                });
                return [];
            }
        },

        uploadContractFiles: async (
            contractId: number,
            files: File[],
            category: ShipmentContractFileCategory = 'documento',
            scope: ShipmentContractFileScope = 'general'
        ): Promise<ShipmentContractFile[] | null> => {
            if (files.length === 0) {
                return null;
            }

            set({ isUploadingContractFiles: true, error: null });

            try {
                const response = await marketplaceService.uploadContractFiles(contractId, files, category, scope);
                await get().fetchContractFiles(contractId);

                set({
                    isUploadingContractFiles: false,
                });

                return normalizeCollection<ShipmentContractFile>(response.data);
            } catch (error) {
                set({
                    isUploadingContractFiles: false,
                    error: toErrorMessage(error, 'No se pudieron subir los archivos del contrato.'),
                });
                return null;
            }
        },

        downloadContractFile: async (
            contractId: number,
            fileId: number,
            defaultFileName?: string
        ): Promise<FileDownloadResult | null> => {
            set({ isDownloadingContractFile: true, error: null });

            try {
                const { blob, fileName } = await marketplaceService.downloadContractFile(contractId, fileId, defaultFileName);
                const downloadMode = await downloadBlobToDevice(blob, fileName);

                set({
                    isDownloadingContractFile: false,
                });

                return downloadMode;
            } catch (error) {
                set({
                    isDownloadingContractFile: false,
                    error: toErrorMessage(error, 'No se pudo descargar el archivo del contrato.'),
                });
                return null;
            }
        },

        deleteContractFile: async (contractId: number, fileId: number): Promise<boolean> => {
            set({ isDeletingContractFile: true, error: null });

            try {
                await marketplaceService.deleteContractFile(contractId, fileId);
                await get().fetchContractFiles(contractId);

                set({
                    isDeletingContractFile: false,
                });

                return true;
            } catch (error) {
                set({
                    isDeletingContractFile: false,
                    error: toErrorMessage(error, 'No se pudo eliminar el archivo del contrato.'),
                });
                return false;
            }
        },

        updateContractPoints: async (
            contractId: number,
            payload: Parameters<typeof marketplaceService.updateContractPoints>[1]
        ): Promise<ShipmentContract | null> => {
            set({ isSavingContract: true, error: null });

            try {
                const response = await marketplaceService.updateContractPoints(contractId, payload);

                set({
                    selectedContract: response.data,
                    isSavingContract: false,
                });

                return response.data;
            } catch (error) {
                set({
                    isSavingContract: false,
                    error: toErrorMessage(error, 'No se pudieron guardar los puntos del contrato.'),
                });
                return null;
            }
        },

        cancelContract: async (contractId: number, reason: string): Promise<ShipmentContract | null> => {
            set({ isSavingContract: true, error: null });

            try {
                const response = await marketplaceService.cancelContract(contractId, { reason });
                const cancelledContract = response.data;

                set((state) => ({
                    selectedContract: cancelledContract,
                    myContracts: mergeContractIntoList(
                        state.myContracts,
                        cancelledContract,
                        state.myContractsStatusFilter
                    ),
                    isSavingContract: false,
                }));

                return cancelledContract;
            } catch (error) {
                set({
                    isSavingContract: false,
                    error: toErrorMessage(error, 'No se pudo cancelar el contrato.'),
                });
                return null;
            }
        },

        sendInvitation: async (
            contractId: number,
            payload: Parameters<typeof marketplaceService.createContractInvitation>[1]
        ): Promise<{ notificationChannel: 'push' | 'email' | null; invitedUserCreated: boolean } | null> => {
            set({ isSendingInvitation: true, error: null });

            try {
                const response = await marketplaceService.createContractInvitation(contractId, payload);
                await get().fetchContractDetail(contractId);

                set({
                    isSendingInvitation: false,
                });

                return {
                    notificationChannel: response.meta?.notification_channel ?? null,
                    invitedUserCreated: response.meta?.invited_user_created === true,
                };
            } catch (error) {
                set({
                    isSendingInvitation: false,
                    error: toErrorMessage(error, 'No se pudo enviar la invitación.'),
                });
                return null;
            }
        },

        acceptInvitation: async (
            token: string
        ): Promise<{ contractId: number; chatConversationId: number | null; inviteStatus?: 'accepted'; idempotent?: boolean } | null> => {
            set({ isSavingContract: true, error: null });

            try {
                const response = await marketplaceService.acceptInvitation(token);

                set({
                    isSavingContract: false,
                });

                return {
                    contractId: response.data.contract_id,
                    chatConversationId: response.data.chat_conversation_id,
                    inviteStatus: response.data.invite_status === 'accepted' ? 'accepted' : undefined,
                    idempotent: response.data.idempotent,
                };
            } catch (error) {
                set({
                    isSavingContract: false,
                    error: toErrorMessage(error, 'No se pudo aceptar la invitación.'),
                });
                return null;
            }
        },

        declineInvitation: async (
            token: string
        ): Promise<{ contractId?: number; inviteStatus?: 'declined'; idempotent?: boolean } | null> => {
            set({ isSavingContract: true, error: null });

            try {
                const response = await marketplaceService.declineInvitation(token);
                set({
                    isSavingContract: false,
                });
                return {
                    contractId: response.data?.contract_id,
                    inviteStatus: response.data?.invite_status,
                    idempotent: response.data?.idempotent,
                };
            } catch (error) {
                set({
                    isSavingContract: false,
                    error: toErrorMessage(error, 'No se pudo rechazar la invitación.'),
                });
                return null;
            }
        },

        decideInvitation: async (
            invitationId: number,
            decision: 'accept' | 'decline'
        ): Promise<{ contractId: number; chatConversationId: number | null; inviteStatus: 'accepted' | 'declined'; idempotent: boolean } | null> => {
            set({ isSavingContract: true, error: null });

            try {
                const response = await marketplaceService.decideInvitation(invitationId, decision);

                set({
                    isSavingContract: false,
                });

                return {
                    contractId: response.data.contract_id,
                    chatConversationId: response.data.chat_conversation_id ?? null,
                    inviteStatus: response.data.invite_status,
                    idempotent: response.data.idempotent,
                };
            } catch (error) {
                set({
                    isSavingContract: false,
                    error: toErrorMessage(error, 'No se pudo responder la invitación.'),
                });
                return null;
            }
        },
    };
}
